// صوت مكالمة الاستديو عبر Cloudflare RealtimeKit.
//
// التحسين للسرعة الفورية:
//  1. تحميل مسبق لحزمة RealtimeKit فور فتح الاستديو أو بدء الرنين.
//  2. تجهيز جلسة الصوت في الخلفية أثناء الرنين — عند الطرفين: المتصل
//     أثناء نغمة الانتظار، والمذيع لأول مكالمة في طابوره.
//  3. عند الردّ لا يبقى إلا فتح الميكروفون.
//
// والقياس الذي دعا إلى تجهيز المذيع: بدونه يبدأ من الصفر حين يضغط «ردّ»
// (توكن ~٢ث، ثم تهيئة المكتبة ~٤ث، ثم الربط والميكروفون)، فلا يسمعه
// المتصل إلا بعد ١١–١٣ ثانية.

import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE, isApiConfigured, getAuthToken } from '../api/client';

const STATES = {
  idle: 'idle',
  connecting: 'connecting',
  live: 'live',
  unavailable: 'unavailable',
  error: 'error',
};

let realtimeKitPromise = null;

/** تحميل مسبق لحزمة الصوت في الذاكرة لمنع تأخير التنزيل لحظة الرد */
/**
 * يبدأ سماع الحاضرين دون انتظار.
 *
 * مشغّل المكتبة عنصر صوت مصدره بثٌّ حيّ. وسفاري (والآيفون كله) لا يحلّ
 * وعد play() حتى تصل أول عيّنة صوت — فمن دخل والجميع صامت كان ينتظر
 * إلى الأبد على «جاري تجهيز الصوت». فالاتصال يُعلَن بعد الانضمام، والتشغيل
 * يمضي وحده. وإن رفضه المتصفح لغياب لمسة، فأول لمسة تعيده.
 */
export function startPlayback(audio) {
  if (!audio) return;
  const unlock = () => audio.play().catch(() => {});
  audio.play().catch(() => {
    window.addEventListener('click', unlock, { once: true });
    window.addEventListener('touchstart', unlock, { once: true });
  });
}

export function preloadRealtimeKit() {
  if (!realtimeKitPromise) {
    realtimeKitPromise = import('@cloudflare/realtimekit')
      .then((m) => m.default || m)
      .catch((err) => {
        realtimeKitPromise = null;
        throw err;
      });
  }
  return realtimeKitPromise;
}

async function fetchCallToken(callId) {
  const authToken = getAuthToken();
  const res = await fetch(`${API_BASE}/api/studio/call-token`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    body: JSON.stringify({ callId }),
  });
  const payload = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error(payload?.error ?? 'تعذر تجهيز الصوت.');
    err.code = payload?.code;
    throw err;
  }
  return payload.token;
}

/** يفتح جلسة المكالمة وينضمّ إليها بميكروفون مغلق. */
async function openMeeting(callId) {
  const [token, RealtimeKitClient] = await Promise.all([
    fetchCallToken(callId),
    preloadRealtimeKit(),
  ]);
  const meeting = await RealtimeKitClient.init({
    authToken: token,
    defaults: { audio: false, video: false },
    modules: { experimentalAudioPlayback: true },
  });
  try {
    await meeting.join();
  } catch (err) {
    // لا تبقى نصف مفتوحة فيصطدم بها من يحاول بعدها بالتوكن نفسه
    meeting.leave().catch(() => {});
    throw err;
  }
  return meeting;
}

/*
  الجلسات المجهّزة مسبقاً، بمعرّف المكالمة.

  على مستوى الوحدة لا داخل الخطّاف: المذيع يجهّز من لوحة المكالمات
  الواردة ثم يردّ من شاشة المكالمة — مكوّنان مختلفان، والجلسة تنتقل
  بينهما. ولكل مكالمة جلسة واحدة على الأكثر: جلستان بتوكن واحد ترفضهما
  Cloudflare بـ ERR0001 «Failed to initialize».
*/
const warmCalls = new Map();

// مهلة قبل ترك جلسة لم يعد أحد يطلبها. تركُها لحظتها يُسقطها في أدقّ
// لحظة: حين يردّ المذيع تخرج المكالمة من الطابور فتتخلّى عنها اللوحة،
// قبل أن تأخذها شاشة المكالمة في الدورة نفسها.
const RELEASE_GRACE_MS = 20_000;

/** يجهّز جلسة المكالمة في الخلفية، مرة واحدة لكل مكالمة. */
export function prewarmCall(callId) {
  if (!callId || !isApiConfigured()) return;

  const existing = warmCalls.get(callId);
  if (existing) {
    clearTimeout(existing.timer);
    existing.timer = null;
    return;
  }

  const entry = { promise: null, timer: null };
  entry.promise = openMeeting(callId).catch((err) => {
    // الفشل هنا لا يمنع المحاولة عند الردّ الفعلي
    console.warn('[studio-call] Prewarm voice notice:', err?.message);
    if (warmCalls.get(callId) === entry) warmCalls.delete(callId);
    return null;
  });
  warmCalls.set(callId, entry);
}

/** يترك الجلسة المجهّزة إن لم يأخذها أحد خلال المهلة. */
export function releasePrewarm(callId, delay = RELEASE_GRACE_MS) {
  const entry = warmCalls.get(callId);
  if (!entry || entry.timer) return;
  entry.timer = setTimeout(() => {
    if (warmCalls.get(callId) !== entry) return;
    warmCalls.delete(callId);
    entry.promise.then((meeting) => meeting?.leave().catch(() => {}));
  }, delay);
}

/**
 * يترك فوراً كل جلسة مجهّزة لمكالمة لم تعد لنا.
 *
 * المذيعون يجهّزون أول مكالمة في الطابور معاً، فإذا ردّ عليها زميلٌ بقي
 * الآخرون في غرفتها حتى تنقضي المهلة — يسمعون مكالمة ليست لهم.
 *
 * @param {Set<string>} keep المكالمات التي ما زالت لنا: المنتظرة ومكالمتنا
 */
export function releaseStalePrewarms(keep) {
  for (const callId of [...warmCalls.keys()]) {
    if (!keep.has(callId)) {
      const entry = warmCalls.get(callId);
      clearTimeout(entry.timer);
      entry.timer = null;
      releasePrewarm(callId, 0);
    }
  }
}

/** يأخذ الجلسة المجهّزة (أو التي ما زالت تُجهَّز) ويخرجها من المخزن. */
function takePrewarmed(callId) {
  const entry = warmCalls.get(callId);
  if (!entry) return null;
  clearTimeout(entry.timer);
  warmCalls.delete(callId);
  return entry.promise;
}

export function useStudioCallVoice({ callId, active, isWaiting = false, isMuted = false }) {
  const [status, setStatus] = useState(STATES.idle);
  const [errorMessage, setErrorMessage] = useState('');
  const [otherSpeaking, setOtherSpeaking] = useState(false);

  const meetingRef = useRef(null);
  const joiningRef = useRef(false);
  // يرتفع مع كل مغادرة: انضمامٌ يكتمل بعدها يعرف أنه قديم فيغادر
  const generationRef = useRef(0);
  // آخر مكالمة لا الحالية: الإلغاء يمسح المعرّف قبل أن تصل المغادرة،
  // فلو قرأت الحالية لما عرفت أي جلسة مجهّزة تترك، فتبقى معلّقة تعيد
  // الاتصال بنفسها (مقيسٌ على الجهاز)
  const callIdRef = useRef(callId);
  if (callId) callIdRef.current = callId;
  // الانضمام يقرأ الكتم لحظته ولا يُعاد بتغيّره، وإلا فتح جلسة ثانية
  const mutedRef = useRef(isMuted);
  mutedRef.current = isMuted;

  const leave = useCallback(async () => {
    const meeting = meetingRef.current;
    meetingRef.current = null;
    joiningRef.current = false;
    generationRef.current += 1;
    // من ألغى وهو ينتظر لا يترك جلسته المجهّزة معلّقة
    if (callIdRef.current) releasePrewarm(callIdRef.current, 0);

    // 1. إيقاف وإغلاق مسارات الميكروفون بالكامل لتحرير عتاد الصوت في الهاتف
    try {
      if (meeting?.self) {
        await meeting.self.disableAudio().catch(() => {});
        const audioTrack = meeting.self.getAudioTrack?.() || meeting.self.audioTrack || meeting.self.rawAudioTrack;
        audioTrack?.stop?.();
        audioTrack?.track?.stop?.();

        if (meeting.self.tracks) {
          const trackList = Array.from(meeting.self.tracks?.values ? meeting.self.tracks.values() : meeting.self.tracks);
          for (const t of trackList) {
            t?.stop?.();
            t?.track?.stop?.();
          }
        }
      }
    } catch {}

    // 2. إيقاف وتفريغ كافة عناصر تشغيل الصوت في الصفحة
    try {
      if (meeting?.audio) {
        meeting.audio.pause?.();
        meeting.audio.srcObject = null;
        meeting.audio.src = '';
        meeting.audio.remove?.();
      }
      if (typeof document !== 'undefined') {
        document.querySelectorAll('audio[id*="dyte"], audio[id*="realtime"], audio[data-realtime]').forEach((el) => {
          try {
            el.pause();
            el.srcObject = null;
            el.src = '';
            el.remove();
          } catch {}
        });
      }
    } catch {}

    // 3. مغادرة الجلسة
    if (meeting) {
      try {
        await meeting.leave();
      } catch {}
    }

    setOtherSpeaking(false);
    setStatus(STATES.idle);
  }, []);

  // تجهيز مسبق أثناء رنين الانتظار
  useEffect(() => {
    if (!callId || !isWaiting) return;
    preloadRealtimeKit().catch(() => {});
    prewarmCall(callId);
  }, [callId, isWaiting]);

  const join = useCallback(async () => {
    if (!isApiConfigured() || !callId) return;
    if (meetingRef.current || joiningRef.current) return;
    joiningRef.current = true;
    const generation = generationRef.current;
    const stale = () => generation !== generationRef.current;

    setStatus(STATES.connecting);
    setErrorMessage('');

    try {
      // المجهّزة مسبقاً إن وُجدت — ولو لم يكتمل تجهيزها بعد ننتظره
      const warm = takePrewarmed(callId);
      let meeting = warm ? await warm : null;
      if (!meeting) meeting = await openMeeting(callId);

      if (stale()) {
        meeting.leave().catch(() => {});
        return;
      }
      meetingRef.current = meeting;
      joiningRef.current = false;

      const refresh = () => {
        const talking = meeting.participants.joined
          .toArray()
          .some((p) => p.audioEnabled);
        setOtherSpeaking(talking);
      };
      meeting.participants.joined.on('audioUpdate', refresh);
      meeting.participants.active.on('participantJoined', refresh);
      meeting.participants.active.on('participantLeft', refresh);
      // من كان يتكلّم قبل أن نستمع لا يُطلق audioUpdate لنا
      refresh();

      if (!mutedRef.current) {
        await meeting.self.enableAudio().catch((err) => {
          console.warn('[studio-call] enableAudio notice:', err);
        });
      }

      setStatus(STATES.live);
      startPlayback(meeting.audio);
    } catch (err) {
      if (stale()) return;
      meetingRef.current = null;
      joiningRef.current = false;
      console.error('[studio-call] فشل الانضمام:', err?.name, err?.message, err);

      if (err.code === 'VOICE_NOT_CONFIGURED') {
        setStatus(STATES.unavailable);
        setErrorMessage('الاتصال الصوتي غير مفعّل في هذه النسخة بعد.');
      } else {
        setStatus(STATES.error);
        if (err?.name === 'NotAllowedError' || err?.name === 'NotFoundError') {
          setErrorMessage('لم يُسمح باستخدام الميكروفون. فعّله من إعدادات الجهاز.');
        } else {
          const detail = [err?.name, err?.message].filter(Boolean).join(': ');
          setErrorMessage(detail ? `تعذّر فتح الصوت — ${detail}` : 'تعذّر فتح الصوت.');
        }
      }
    }
  }, [callId]);

  useEffect(() => {
    if (active && callId) {
      join();
    } else if (!active && !isWaiting) {
      leave();
    }
  }, [active, isWaiting, callId, join, leave]);

  useEffect(() => {
    const meeting = meetingRef.current;
    if (!meeting || status !== STATES.live) return;
    const apply = async () => {
      try {
        if (isMuted) await meeting.self.disableAudio();
        else await meeting.self.enableAudio();
      } catch {
        // تجاهل في حال سحب الإذن
      }
    };
    apply();
  }, [isMuted, status]);

  useEffect(() => () => void leave(), [leave]);

  return {
    voiceStatus: status,
    voiceError: errorMessage,
    isVoiceLive: status === STATES.live,
    otherSpeaking,
  };
}
